import i18n from '@/utils/i18n';
import {
  formatCurrency,
  getNormalizedPriceInMicros,
  MICRO_CONVERSION,
} from '@/utils/money';
import { InputValidationResult } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-input';
import { parseTransientAwareNumber } from '@sales-portal/domain/pricing/screens/management/blocs/utils/numeric-input';
import { PricingEditField } from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';

interface Config {
  costInMicros: number;
  currencyCode: string;
  enabled?: boolean;
  inputType: PricingEditField;
  marginUpperBound?: number;
}

const validatePriceFloor = (
  priceInMicros: number,
  costInMicros: number,
  currencyCode: string,
): InputValidationResult => {
  // Compare at whole-cent precision so input == floor doesn't trip
  // floating-point error (e.g. 8.26 * 1_000_000 = 8259999.999...).
  const normalizedPriceInMicros = getNormalizedPriceInMicros(priceInMicros);
  const normalizedCostInMicros = getNormalizedPriceInMicros(costInMicros);
  if (
    normalizedPriceInMicros !== null &&
    normalizedCostInMicros !== null &&
    normalizedPriceInMicros < normalizedCostInMicros
  ) {
    return {
      message: i18n.t('pricing.table.validation.price_below_cost', {
        cost: formatCurrency(costInMicros, currencyCode),
      }),
      valid: false,
    };
  }
  return { valid: true };
};

const validateMarginFloor = (
  grossMarginPercentage: number,
  marginUpperBound: number | undefined,
): InputValidationResult => {
  if (
    grossMarginPercentage <= 0 ||
    (marginUpperBound !== undefined &&
      grossMarginPercentage >= marginUpperBound)
  ) {
    return {
      message: i18n.t('pricing.table.validation.margin_must_be_positive'),
      valid: false,
    };
  }
  return { valid: true };
};

export const buildPricingInputValidator =
  ({
    costInMicros,
    currencyCode,
    enabled = true,
    inputType,
    marginUpperBound,
  }: Config) =>
  (text: string): InputValidationResult => {
    if (!enabled) {
      return { valid: true };
    }
    const parsed = parseTransientAwareNumber(text);
    if (parsed === null) {
      return { valid: true };
    }

    if (inputType === PricingEditField.Price) {
      return validatePriceFloor(
        parsed * MICRO_CONVERSION,
        costInMicros,
        currencyCode,
      );
    }

    return validateMarginFloor(parsed, marginUpperBound);
  };

export const validatePricingFloor = ({
  costInMicros,
  currencyCode,
  grossMarginPercentage,
  inputType,
  marginUpperBound,
  priceInMicros,
}: Config & {
  grossMarginPercentage: number;
  priceInMicros: number;
}): InputValidationResult => {
  return inputType === PricingEditField.Price
    ? validatePriceFloor(priceInMicros, costInMicros, currencyCode)
    : validateMarginFloor(grossMarginPercentage, marginUpperBound);
};
